import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useFetch, useIsMounted } from 'app/utils/hooks';
import { buildQueryUrl } from 'app/utils/utils';
import { Link } from 'types/api.types';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setLoadMore, setSearchNextLink } from 'app/store/Profile/slice';
import {
  getSearchNextlink,
  loadMoreSearchResults,
} from 'app/store/Profile/selectors';

export const useChakraAsyncAutocompleteSearch = <O extends {}, R extends {}>(
  getResponseItems: (response: R, data: O[]) => O[],
  fetchCallback?: (value: string) => void,
  getNextResponseLink?: (response: R) => Link
) => {
  const isMounted = useIsMounted();
  const { callAPI } = useFetch<string, R>();
  const [options, setOptions] = useState<O[]>([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const nextLink = useAppSelector(getSearchNextlink);
  const loadMoreResults = useAppSelector(loadMoreSearchResults);

  const clearOptions = () => {
    dispatch(setSearchNextLink({ next: undefined }));
    setOptions([]);
    setLoading(false);
  };

  const preFetch = useCallback(() => {
    if (!isMounted()) {
      return;
    }
    dispatch(setSearchNextLink({ next: undefined }));
    setOptions([]);
    setLoading(true);
  }, [dispatch, isMounted]);

  const fetchSearchResults = useMemo(
    () =>
      debounce(async (search: string, append: boolean) => {
        if (!isMounted()) {
          return;
        }
        try {
          const response = await callAPI(search);
          if (!isMounted() || !response) {
            return;
          }
          const next = getNextResponseLink?.(response);
          dispatch(setSearchNextLink({ next }));
          if (fetchCallback) {
            fetchCallback(search);
          }
          setOptions((options) => {
            const items = getResponseItems(response, options);
            if (!items?.length) {
              return [];
            }

            return append ? [...options, ...items] : items;
          });
        } finally {
          setLoading(false);
        }
      }, 300),
    [
      isMounted,
      callAPI,
      getNextResponseLink,
      dispatch,
      fetchCallback,
      getResponseItems,
    ]
  );

  const fetch = useCallback(
    (search: string, append = false) => {
      if (!append) {
        preFetch();
      }
      setLoading(true);
      return fetchSearchResults(buildQueryUrl(search), append);
    },
    [fetchSearchResults, preFetch]
  );

  useEffect(() => {
    if (nextLink && loadMoreResults) {
      fetch(nextLink.href, true);
      dispatch(setLoadMore(false));
    }
  }, [dispatch, fetch, loadMoreResults, nextLink]);

  return {
    fetch,
    options,
    loading,
    clearOptions,
  };
};
